import type { IconName } from './icons.ts';

/** Taille de référence d'une tuile, en pixels (éditeur, calques hors écran). */
export const TILE_SIZE = 32;

export type TileKey =
  | 'grass' | 'tallgrass' | 'path' | 'sand' | 'water' | 'tree' | 'rock' | 'flowers'
  | 'wood' | 'heal' | 'bridge' | 'wall' | 'roof' | 'door' | 'cave' | 'fence' | 'shop'
  | 'indoor_wall' | 'carpet' | 'counter' | 'plant' | 'gym_roof' | 'pc';

/** Ce qui se passe quand le joueur, face à la tuile, appuie sur la touche d'action. */
export type Interaction = 'heal' | 'shop' | 'storage' | 'wardrobe' | 'read' | 'drop' | 'use';

export const INTERACTIONS: Readonly<Record<Interaction, { label: string; icon: IconName }>> = {
  heal: { label: 'Soigner vos {monstres}', icon: 'heal' },
  shop: { label: 'Ouvrir la boutique', icon: 'shop' },
  storage: { label: 'Accéder à la boîte', icon: 'storage' },
  wardrobe: { label: 'Changer d\'apparence', icon: 'appearance' },
  read: { label: 'Lire', icon: 'info' },
  drop: { label: 'Fouiller', icon: 'chest' },
  // En jeu, l'invite nomme l'objet demandé (« Utiliser Capsule »), et n'apparaît que pendant la quête qui le demande.
  use: { label: 'Utiliser un objet', icon: 'bag' },
};

export interface TileDefinition {
  readonly id: number;
  readonly key: TileKey;
  readonly name: string;
  readonly walkable: boolean;
  /** Objet utilisable en se plaçant face à lui (il n'est jamais praticable). */
  readonly interaction?: Interaction;
}

/**
 * Catalogue des éléments de base (identifiants et clés), utilisé par le contenu
 * de démo pour dessiner ses cartes. En jeu, tout vient des éléments (voir map-elements.ts) : leur
 * rendu, leurs collisions et leurs interactions se règlent dans l'éditeur d'éléments.
 * L'index dans ce tableau est l'identifiant stocké dans les cartes : ne jamais réordonner. */
export const TILES: readonly TileDefinition[] = [
  { id: 0, key: 'grass', name: 'Herbe', walkable: true },
  { id: 1, key: 'tallgrass', name: 'Hautes herbes', walkable: true },
  { id: 2, key: 'path', name: 'Chemin', walkable: true },
  { id: 3, key: 'sand', name: 'Sable', walkable: true },
  { id: 4, key: 'water', name: 'Eau', walkable: false },
  { id: 5, key: 'tree', name: 'Arbre', walkable: false },
  { id: 6, key: 'rock', name: 'Rocher', walkable: false },
  { id: 7, key: 'flowers', name: 'Fleurs', walkable: true },
  { id: 8, key: 'wood', name: 'Parquet', walkable: true },
  { id: 9, key: 'heal', name: 'Borne de soin', walkable: false, interaction: 'heal' },
  { id: 10, key: 'bridge', name: 'Pont', walkable: true },
  { id: 11, key: 'wall', name: 'Mur', walkable: false },
  { id: 12, key: 'roof', name: 'Toit', walkable: false },
  { id: 13, key: 'door', name: 'Porte', walkable: true },
  { id: 14, key: 'cave', name: 'Sol de grotte', walkable: true },
  { id: 15, key: 'fence', name: 'Barrière', walkable: false },
  { id: 16, key: 'shop', name: 'Comptoir boutique', walkable: false, interaction: 'shop' },
  { id: 17, key: 'indoor_wall', name: 'Mur intérieur', walkable: false },
  { id: 18, key: 'carpet', name: 'Tapis', walkable: true },
  { id: 19, key: 'counter', name: 'Comptoir', walkable: false },
  { id: 20, key: 'plant', name: 'Plante', walkable: false },
  { id: 21, key: 'gym_roof', name: 'Toit d\'arène', walkable: false },
  { id: 22, key: 'pc', name: 'PC de stockage (boîte)', walkable: false, interaction: 'storage' },
];

const tileIdByKey = new Map(TILES.map((tile) => [tile.key, tile.id]));

export function tileId(key: TileKey): number {
  const id = tileIdByKey.get(key);
  if (id === undefined) throw new Error(`Tuile inconnue : ${key}`);
  return id;
}

/** Praticable selon le catalogue de base (contenu de démo uniquement). */
export const isBaseWalkable = (id: number): boolean => TILES[id]?.walkable ?? false;
